import React, { FC, useState } from 'react';
import { useFormik } from 'formik';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '../../components/bootstrap/Modal';
import Button from '../../components/bootstrap/Button';
import FormGroup from '../../components/bootstrap/forms/FormGroup';
import Input from '../../components/bootstrap/forms/Input';
import Icon from '../../components/icon/Icon';
import showNotification from '../../components/extras/showNotification';
import UserProfileService from '../../services/UserProfileService';

interface IChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface IChangePasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const validate = (values: IChangePasswordForm) => {
  const errors: Partial<IChangePasswordForm> = {};

  if (!values.currentPassword) {
    errors.currentPassword = 'Current password is required';
  }

  if (!values.newPassword) {
    errors.newPassword = 'New password is required';
  } else if (values.newPassword.length < 8) {
    errors.newPassword = 'Password must be at least 8 characters';
  } else if (!/[A-Z]/.test(values.newPassword)) {
    errors.newPassword = 'Password must contain at least one uppercase letter';
  } else if (!/[0-9]/.test(values.newPassword)) {
    errors.newPassword = 'Password must contain at least one number';
  }

  if (!values.confirmPassword) {
    errors.confirmPassword = 'Please confirm your new password';
  } else if (values.confirmPassword !== values.newPassword) {
    errors.confirmPassword = 'Passwords do not match';
  }

  return errors;
};

/**
 * ChangePasswordModal — allows the logged-in user to update their password
 * by providing their current password and a new password meeting policy requirements.
 */
const ChangePasswordModal: FC<IChangePasswordModalProps> = ({ isOpen, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const formik = useFormik<IChangePasswordForm>({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validate,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      try {
        await UserProfileService.changePassword(values.currentPassword, values.newPassword);
        showNotification(
          <span className='d-flex align-items-center gap-2'>
            <Icon icon='CheckCircle' color='success' />
            Password Changed
          </span>,
          'Your password has been updated successfully.',
          'success',
        );
        resetForm();
        onClose();
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : 'Failed to change password. Please try again.';
        showNotification(
          <span className='d-flex align-items-center gap-2'>
            <Icon icon='ErrorOutline' color='danger' />
            Change Failed
          </span>,
          message,
          'danger',
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  /** Password strength meter — returns score 0-4 */
  const getPasswordStrength = (password: string): number => {
    if (!password) return 0;
    let score = 0;
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score += 1;
    if (/[0-9]/.test(password) && /[^A-Za-z0-9]/.test(password)) score += 1;
    return score;
  };

  const strengthScore = getPasswordStrength(formik.values.newPassword);
  const strengthConfig: Record<number, { label: string; color: string; width: string }> = {
    0: { label: '', color: '#e9ecef', width: '0%' },
    1: { label: 'Weak', color: '#dc3545', width: '25%' },
    2: { label: 'Fair', color: '#fd7e14', width: '50%' },
    3: { label: 'Good', color: '#ffc107', width: '75%' },
    4: { label: 'Strong', color: '#198754', width: '100%' },
  };
  const strength = strengthConfig[strengthScore];

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={handleClose}
      size='sm'
      titleId='change-password-modal'
      isStaticBackdrop={false}
      isCentered
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.85 }}
      transition={{ ease: 'easeOut', duration: 0.18 }}>
      <ModalHeader setIsOpen={handleClose}>
        <ModalTitle id='change-password-modal'>
          <span className='d-flex align-items-center gap-2'>
            <span
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Icon icon='Lock' color='light' size='sm' />
            </span>
            Change Password
          </span>
        </ModalTitle>
      </ModalHeader>

      <form onSubmit={formik.handleSubmit} noValidate>
        <ModalBody>
          {/* Security note */}
          <div
            className='d-flex align-items-start gap-2 rounded-2 p-3 mb-4'
            style={{
              background: 'rgba(79, 70, 229, 0.06)',
              border: '1px solid rgba(79, 70, 229, 0.18)',
            }}>
            <Icon icon='InfoOutlined' color='primary' className='flex-shrink-0 mt-1' />
            <p className='mb-0 small text-muted'>
              Your password must be at least 8 characters and include an uppercase letter and a
              number.
            </p>
          </div>

          {/* Current Password */}
          <FormGroup
            id='currentPassword'
            label='Current Password'
            className='mb-3'
            isFloating={false}>
            <div className='input-group'>
              <Input
                id='currentPassword'
                type={showCurrent ? 'text' : 'password'}
                name='currentPassword'
                placeholder='Enter current password'
                value={formik.values.currentPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isValid={
                  formik.touched.currentPassword && !formik.errors.currentPassword
                }
                invalidFeedback={formik.errors.currentPassword}
              />
              <button
                type='button'
                className='btn btn-outline-secondary'
                onClick={() => setShowCurrent((v) => !v)}
                tabIndex={-1}
                aria-label='Toggle current password visibility'>
                <Icon icon={showCurrent ? 'VisibilityOff' : 'Visibility'} />
              </button>
            </div>
          </FormGroup>
          {formik.touched.currentPassword && formik.errors.currentPassword && (
            <div className='invalid-feedback d-block mt-1'>{formik.errors.currentPassword}</div>
          )}

          {/* New Password */}
          <FormGroup id='newPassword' label='New Password' className='mb-1' isFloating={false}>
            <div className='input-group'>
              <Input
                id='newPassword'
                type={showNew ? 'text' : 'password'}
                name='newPassword'
                placeholder='Enter new password'
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isValid={formik.touched.newPassword && !formik.errors.newPassword}
                invalidFeedback={formik.errors.newPassword}
              />
              <button
                type='button'
                className='btn btn-outline-secondary'
                onClick={() => setShowNew((v) => !v)}
                tabIndex={-1}
                aria-label='Toggle new password visibility'>
                <Icon icon={showNew ? 'VisibilityOff' : 'Visibility'} />
              </button>
            </div>
          </FormGroup>
          {formik.touched.newPassword && formik.errors.newPassword && (
            <div className='invalid-feedback d-block mt-1'>{formik.errors.newPassword}</div>
          )}

          {/* Password strength bar */}
          {formik.values.newPassword && (
            <div className='mb-3'>
              <div
                className='rounded-pill overflow-hidden'
                style={{ height: 4, background: '#e9ecef' }}>
                <div
                  style={{
                    height: '100%',
                    width: strength.width,
                    background: strength.color,
                    transition: 'width 0.3s ease, background 0.3s ease',
                  }}
                />
              </div>
              {strength.label && (
                <small style={{ color: strength.color, fontWeight: 500 }}>
                  {strength.label} password
                </small>
              )}
            </div>
          )}

          {/* Confirm Password */}
          <FormGroup
            id='confirmPassword'
            label='Confirm New Password'
            className='mb-0'
            isFloating={false}>
            <div className='input-group'>
              <Input
                id='confirmPassword'
                type={showConfirm ? 'text' : 'password'}
                name='confirmPassword'
                placeholder='Repeat new password'
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isValid={
                  formik.touched.confirmPassword && !formik.errors.confirmPassword
                }
                invalidFeedback={formik.errors.confirmPassword}
              />
              <button
                type='button'
                className='btn btn-outline-secondary'
                onClick={() => setShowConfirm((v) => !v)}
                tabIndex={-1}
                aria-label='Toggle confirm password visibility'>
                <Icon icon={showConfirm ? 'VisibilityOff' : 'Visibility'} />
              </button>
            </div>
          </FormGroup>
          {formik.touched.confirmPassword && formik.errors.confirmPassword && (
            <div className='invalid-feedback d-block mt-1'>{formik.errors.confirmPassword}</div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button color='light' onClick={handleClose} isDisable={isSubmitting}>
            Cancel
          </Button>
          <Button
            type='submit'
            color='primary'
            className='d-inline-flex align-items-center gap-2'
            isDisable={isSubmitting}
            style={{
              background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
              border: 'none',
            }}>
            {isSubmitting ? (
              <>
                <span
                  className='spinner-border spinner-border-sm'
                  role='status'
                  aria-hidden='true'
                />
                Updating...
              </>
            ) : (
              <>
                <Icon icon='Lock' />
                Update Password
              </>
            )}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default ChangePasswordModal;
